import { useEffect } from 'react';

import type {
  ApiABExperimentBucketForId,
  ApiABExperimentId,
} from '#/api/abExperiment/types/ApiABExperiment';
import { apiABExperiments } from '#/api/abExperiment/types/apiABExperiments';
import { useAppConfig } from '#/config/AppConfigContext';
import {
  useClientEnvironment,
  useUserEnvironmentActions,
} from '#/contexts/UserEnvironmentContext';
import { rollABExperiment } from '#/hooks/abExperiment/rollABExperiment';
import { useIsClient } from '#/hooks/useIsClient';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { assignAnalyticsAbExperimentBucket } from '#/utils/analytics/abExperiments/assignAnalyticsAbExperimentBucket';
import { log } from '#/utils/logging';

/**
 * Client assignments is here so multiple uses of the same experiment always share the same value
 */
const clientAssignments: {
  [experimentId in ApiABExperimentId]?: {
    bucket: ApiABExperimentBucketForId<experimentId>;
    dispatched: boolean;
  };
} = {};

/**
 * Returns a bucket for the given `experimentId` if the user environment is ready,
 * otherwise returns the default bucket
 */
export function useAbExperimentBucket<TExperimentId extends ApiABExperimentId>(
  experimentId: TExperimentId,
): ApiABExperimentBucketForId<TExperimentId> {
  const { appConfig } = useAppConfig();
  const { setABExperimentBucket } = useUserEnvironmentActions();
  const { clientEnvironment } = useClientEnvironment();
  const isClient = useIsClient();

  // Experiment config
  const experimentConfig = appConfig.idealist.abExperiments.find(
    (experiment) => experiment.id === experimentId,
  );

  const { isFirstRenderRef } = useIsFirstRenderRef();
  if (!experimentConfig && isFirstRenderRef.current) {
    log.error(
      `Unable to find experiment config for experimentId "${experimentId}"`,
    );
  }

  // Current bucket
  const environmentExperiment = clientEnvironment?.abExperiments.find(
    (experiment) => experiment.id === experimentId,
  );

  if (
    isClient &&
    clientEnvironment &&
    !environmentExperiment &&
    experimentConfig &&
    !clientAssignments[experimentId]
  ) {
    clientAssignments[experimentId] = {
      bucket: rollABExperiment(experimentConfig.buckets),
      dispatched: false,
    };
  }

  const currentBucket =
    environmentExperiment?.bucket ??
    clientAssignments[experimentId]?.bucket ??
    apiABExperiments[experimentId].defaultBucket;

  // Update analytics as soon as possible to make sure we have the latest data instead of `undefined`
  assignAnalyticsAbExperimentBucket(experimentId, currentBucket);

  useEffect(() => {
    if (
      clientAssignments[experimentId] &&
      !clientAssignments[experimentId].dispatched
    ) {
      clientAssignments[experimentId].dispatched = true;
      void setABExperimentBucket(experimentId, currentBucket);
    }
  }, [experimentId, currentBucket, setABExperimentBucket]);

  return currentBucket;
}
